import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTickets } from '../hooks/useSupport';
import { TICKET_STATUS, TICKET_PRIORITY, TICKET_CATEGORY } from '../services/supportService';
import TicketDetail from '../components/support/TicketDetail';
import { useNotifications } from '../context/NotificationContext';
import TicketStats from '../components/support/TicketStats';
import TicketStatusBadge from '../components/support/TicketStatusBadge';
import TicketPriorityBadge from '../components/support/TicketPriorityBadge';
import { formatRelativeDate } from '../utils/relativeTime';
import PresenceDot from '../components/ui/PresenceDot';

// Filtres rapides (chips) — même esprit que l'onglet Demandes
const STATUS_CHIPS = [
  { value: 'all', label: 'Toutes' },
  { value: TICKET_STATUS.NEW, label: 'Nouveaux' },
  { value: TICKET_STATUS.IN_PROGRESS, label: 'En cours' },
  { value: TICKET_STATUS.RESOLVED, label: 'Résolus' },
  { value: TICKET_STATUS.CLOSED, label: 'Fermés' }
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'Toutes priorités' },
  { value: TICKET_PRIORITY.LOW, label: 'Faible' },
  { value: TICKET_PRIORITY.MEDIUM, label: 'Moyenne' },
  { value: TICKET_PRIORITY.HIGH, label: 'Haute' },
  { value: TICKET_PRIORITY.URGENT, label: 'Urgente' }
];

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'Toutes catégories' },
  { value: TICKET_CATEGORY.BUG, label: 'Bug' },
  { value: TICKET_CATEGORY.FEATURE_REQUEST, label: 'Fonctionnalité' },
  { value: TICKET_CATEGORY.ACCOUNT, label: 'Compte' },
  { value: TICKET_CATEGORY.BILLING, label: 'Facturation' },
  { value: TICKET_CATEGORY.OTHER, label: 'Autre' }
];

const matchesTicket = (ticket, search) => {
  if (!search) return true;
  const term = search.toLowerCase();
  return [
    ticket.title,
    ticket.description,
    ticket.category,
    ticket.userInfo?.displayName,
    ticket.userInfo?.email
  ].filter(Boolean).some((value) => value.toLowerCase().includes(term));
};

const SupportTicketsPage = () => {
  const location = useLocation();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { unreadTicketKeys } = useNotifications();

  // Deep-link : /support?userId=...&ticketId=... → ouvre directement le ticket
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('userId');
    const ticketId = params.get('ticketId');
    if (userId && ticketId) {
      setSelectedTicket({ userId, id: ticketId });
    }
  }, [location.search]);

  // Options de filtre envoyées au serveur (status, priorité, catégorie)
  const filterOptions = useMemo(() => {
    const options = {};
    if (filterStatus !== 'all') options.status = [filterStatus];
    if (filterPriority !== 'all') options.priority = filterPriority;
    if (filterCategory !== 'all') options.category = filterCategory;
    return options;
  }, [filterStatus, filterPriority, filterCategory]);

  const {
    tickets,
    isLoading,
    isError,
    error,
    hasMore,
    fetchMoreTickets,
    ticketsCount,
    isLoadingCount
  } = useTickets(filterOptions);

  // Recherche locale (titre, description, client) sur la liste chargée
  const visibleTickets = useMemo(
    () => tickets.filter((ticket) => matchesTicket(ticket, isSearching ? searchText : '')),
    [tickets, searchText, isSearching]
  );

  const handleFilterStatus = (value) => {
    setFilterStatus(value);
    setSelectedTicket(null);
  };

  const resetAll = () => {
    setFilterStatus('all');
    setFilterPriority('all');
    setFilterCategory('all');
    setSearchText('');
    setIsSearching(false);
    setSelectedTicket(null);
  };

  // ---- Ligne « ticket » (desktop) ----
  const renderTicketRow = (ticket) => (
    <tr
      key={ticket.id}
      onClick={() => setSelectedTicket(ticket)}
      className="border-t border-base-200 hover:bg-base-200/50 cursor-pointer align-top"
    >
      <td className="px-4 py-3">
        <div className="flex items-start gap-1">
          {ticket.status === TICKET_STATUS.NEW && (
            <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-warning flex-shrink-0" title="Nouveau"></span>
          )}
          {unreadTicketKeys.includes(`${ticket.userId}::${ticket.id}`) && (
            <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-error flex-shrink-0" title="Réponse du client"></span>
          )}
          <div className="min-w-0">
            <p className="font-medium text-base-content truncate max-w-xs">{ticket.title}</p>
            {ticket.description && (
              <p className="text-xs text-base-content/60 truncate max-w-xs mt-0.5">{ticket.description}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <PresenceDot uid={ticket.userId} />
          <Link
            to={`/clients/${ticket.userId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-base-content hover:text-primary hover:underline"
            title="Voir la fiche client"
          >
            {ticket.userInfo?.displayName}
          </Link>
        </div>
        <p className="text-xs text-base-content/60 truncate">{ticket.userInfo?.email}</p>
      </td>
      <td className="px-4 py-3"><TicketStatusBadge status={ticket.status} /></td>
      <td className="px-4 py-3"><TicketPriorityBadge priority={ticket.priority} /></td>
      <td className="px-4 py-3 text-sm text-base-content/70 whitespace-nowrap">
        {formatRelativeDate(ticket.updatedAt)}
      </td>
    </tr>
  );

  // ---- Carte mobile ----
  const renderMobileTicketCard = (ticket) => (
    <div
      key={ticket.id}
      onClick={() => setSelectedTicket(ticket)}
      className="bg-base-100 rounded-lg shadow-sm p-4 mb-3 border-l-4 border-primary cursor-pointer"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-base-content truncate">{ticket.title}</p>
        {ticket.status === TICKET_STATUS.NEW && (
          <span className="inline-block w-2 h-2 rounded-full bg-warning flex-shrink-0"></span>
        )}
        {unreadTicketKeys.includes(`${ticket.userId}::${ticket.id}`) && (
          <span className="inline-block w-2 h-2 rounded-full bg-error flex-shrink-0" title="Réponse du client"></span>
        )}
      </div>
      <p className="flex items-center gap-1.5 text-sm text-base-content/70 mt-0.5 truncate">
        <PresenceDot uid={ticket.userId} />
        <span className="truncate">{ticket.userInfo?.displayName} · {ticket.userInfo?.email}</span>
      </p>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <TicketStatusBadge status={ticket.status} />
        <TicketPriorityBadge priority={ticket.priority} />
        <span className="text-xs text-base-content/50">màj {formatRelativeDate(ticket.updatedAt)}</span>
      </div>
    </div>
  );

  const handleSearchChange = (value) => {
    setSearchText(value);
    setIsSearching(value.trim() !== '');
  };

  // Vue détail
  if (selectedTicket) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary mb-2">Support</h1>
          <div className="text-sm breadcrumbs text-base-content/60">
            <ul><li>MSD Admin</li><li>Support</li><li>Ticket</li></ul>
          </div>
        </div>
        <div className="bg-base-100 rounded-lg shadow-sm p-6">
          <TicketDetail
            userId={selectedTicket.userId}
            ticketId={selectedTicket.id}
            onBack={() => setSelectedTicket(null)}
            key={`ticket-${selectedTicket.id}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary mb-2">Support</h1>
        <div className="text-sm breadcrumbs text-base-content/60">
          <ul><li>MSD Admin</li><li>Support</li></ul>
        </div>
      </div>

      <TicketStats counts={ticketsCount} isLoading={isLoadingCount} />

      <div className="bg-base-100 rounded-lg shadow-sm mb-6">
        <div className="p-4 md:p-6 border-b border-base-200">
          <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 gap-4">
            <div>
              <h2 className="text-xl font-bold text-base-content">Tickets</h2>
              <p className="text-sm text-base-content/60 mt-1">{visibleTickets.length} ticket(s) affiché(s)</p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-grow md:w-80">
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Rechercher (titre, contenu, client…)"
                  className="input input-bordered w-full pr-9"
                />
                {isSearching && (
                  <button
                    onClick={() => handleSearchChange('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-base-content/50 hover:text-base-content/80"
                    title="Effacer"
                  >
                    ✕
                  </button>
                )}
              </div>
              {(isSearching || filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all') && (
                <button onClick={resetAll} className="btn btn-outline whitespace-nowrap">Réinitialiser</button>
              )}
            </div>
          </div>

          {/* Chips statuts (style Demandes) */}
          <div className="flex flex-wrap gap-2 mb-3">
            {STATUS_CHIPS.map((chip) => (
              <button
                key={chip.value}
                onClick={() => handleFilterStatus(chip.value)}
                className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  filterStatus === chip.value
                    ? 'bg-primary text-primary-content'
                    : 'bg-base-200 hover:bg-base-300 text-base-content'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Filtres secondaires (priorité / catégorie) */}
          <div className="flex flex-wrap gap-2">
            <select
              value={filterPriority}
              onChange={(e) => { setFilterPriority(e.target.value); setSelectedTicket(null); }}
              className="select select-bordered select-sm"
            >
              {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setSelectedTicket(null); }}
              className="select select-bordered select-sm"
            >
              {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="p-0 md:p-4">
          {isLoading ? (
            <div className="text-center py-16">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <p className="mt-4 text-base-content/70">Chargement des tickets...</p>
            </div>
          ) : isError ? (
            <div className="text-center py-16">
              <p className="text-error font-medium">Erreur lors du chargement des tickets.</p>
              <p className="text-sm mt-2 text-base-content/70">{error?.message || 'Veuillez réessayer.'}</p>
            </div>
          ) : visibleTickets.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-base-content font-medium">Aucun ticket ne correspond à votre recherche.</p>
              {(isSearching || filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all') && (
                <button onClick={resetAll} className="btn btn-outline btn-sm mt-4">Réinitialiser les filtres</button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile */}
              <div className="md:hidden px-2 pt-2">{visibleTickets.map((t) => renderMobileTicketCard(t))}</div>

              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-base-200/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Ticket</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Statut</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Priorité</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Mise à jour</th>
                    </tr>
                  </thead>
                  <tbody>{visibleTickets.map((t) => renderTicketRow(t))}</tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {!isLoading && visibleTickets.length > 0 && hasMore && (
          <div className="p-4 border-t border-base-200 flex justify-center">
            <button onClick={fetchMoreTickets} className="btn btn-outline btn-sm">
              Charger plus de tickets
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketsPage;

